import React, { useEffect, useRef } from 'react';
import { RichText } from '@lexkit/editor';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { applyStoredContentToEditor } from './rich-text-apply';
import {
  RichTextProvider,
  richTextContentSx,
  richTextEditorConfig,
  richTextExtensions,
  useRichTextEditor,
} from './rich-text-system';

interface RichTextViewProps {
  /** Serialized Lexical JSON (or legacy plain text) to render read-only. */
  value: string;
  /** Optional style overrides merged after the base content styles (e.g. to match a caller's typography). */
  muiStyling?: SxProps<Theme>;
}

function RichTextViewInner({ value, muiStyling }: RichTextViewProps) {
  const { editor } = useRichTextEditor();
  const appliedValueRef = useRef<string | null>(null);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(false);
    if (value === appliedValueRef.current) return;
    appliedValueRef.current = value;
    applyStoredContentToEditor(editor, value);
  }, [editor, value]);

  return (
    <Box
      sx={[
        richTextContentSx,
        { '& [contenteditable]': { outline: 'none' } },
        ...(Array.isArray(muiStyling) ? muiStyling : [muiStyling]),
      ]}
    >
      <RichText />
    </Box>
  );
}

/**
 * Read-only renderer for stored rich-text content. Uses the same LexKit editor system as the Gold
 * app's editor (with the editor set non-editable) so Gold-authored comments render with identical
 * formatting, with no `dangerouslySetInnerHTML`. Legacy plain-text values render as paragraphs.
 * Pass `muiStyling` to tune sizing/spacing for a given caller.
 *
 * Ported (view path only) from cashpile/apps/cashvest/src/components/ui/rich-text.
 */
export function RichTextView({ value, muiStyling }: RichTextViewProps) {
  return (
    <RichTextProvider extensions={richTextExtensions} config={richTextEditorConfig}>
      <RichTextViewInner value={value} muiStyling={muiStyling} />
    </RichTextProvider>
  );
}

export default RichTextView;
